/**
 * MassnahmenOverlayContent Component
 * Interactive Customer Journey overlay with phase overview, tab navigation,
 * touchpoint icon cards, and detail panel with freemium Massnahmen list.
 */

import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Target, FileText, ChevronDown, CheckCircle2,
  Globe, MapPin, Lightbulb, Info,
  // Touchpoint icons
  Share2, Search, Play, Mail, Newspaper, Radio, Building2,
  Star, Phone, UserPlus, Video, BookOpen, Layout, Megaphone,
  PenTool, Headphones, Heart, Tag, BarChart3, MessageCircle,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { FUNNEL_CATEGORIES, normalizeSchritte } from '@/types/massnahmen';
import type { MassnahmenFullData, MassnahmeTableEntry, FunnelCategory, SchrittLeitfaden } from '@/types/massnahmen';
import { SectionDescriptionText } from '@/components/results/SectionDescriptionText';
import type { GetDescriptionFn } from '@/types/sectionDescription';
import { useProjectPlanDialogStore } from '@/stores/projectPlanDialogStore';
export interface MassnahmenOverlayContentProps {
  data: MassnahmenFullData | string;
  getDescription?: GetDescriptionFn;
  featureName?: string;
  onPremiumClick?: () => void;
}

// ── Colors ──────────────────────────────────────────────────────────

const JOURNEY_COLORS: Record<FunnelCategory, string> = {
  Awareness: '#d91680',
  Consideration: '#e84470',
  Conversion: '#f5693d',
  Loyalty: '#ff8c00',
};


// ── Icon mapping ────────────────────────────────────────────────────

function getMeasureIcon(name: string): LucideIcon {
  const n = name.toLowerCase();
  if (n.includes('social media') || n.includes('social-media') || n.includes('branding')) return Share2;
  if (n.includes('google') || n.includes('suchmaschine') || n.includes('seo')) return Search;
  if (n.includes('youtube') || n.includes('video')) return Play;
  if (n.includes('website') || n.includes('web')) return Globe;
  if (n.includes('blog') || n.includes('content') || n.includes('artikel')) return PenTool;
  if (n.includes('e-mail') || n.includes('email') || n.includes('newsletter') || n.includes('mail')) return Mail;
  if (n.includes('pr') || n.includes('presse') || n.includes('press')) return Newspaper;
  if (n.includes('radio') || n.includes('print') || n.includes('anzeige')) return Radio;
  if (n.includes('messe') || n.includes('event') || n.includes('veranstaltung')) return Building2;
  if (n.includes('bewertung') || n.includes('rating') || n.includes('vergleich')) return Star;
  if (n.includes('telefon') || n.includes('call')) return Phone;
  if (n.includes('vor-ort') || n.includes('vor ort') || n.includes('office') || n.includes('beratung im')) return MapPin;
  if (n.includes('empfehlung') || n.includes('referral') || n.includes('weiterempfehlung')) return UserPlus;
  if (n.includes('webinar') || n.includes('seminar')) return Video;
  if (n.includes('case stud') || n.includes('fallstudie')) return BookOpen;
  if (n.includes('landing') || n.includes('optimierung')) return Layout;
  if (n.includes('kampagne') || n.includes('ads') || n.includes('werbung') || n.includes('retargeting')) return Megaphone;
  if (n.includes('podcast') || n.includes('audio') || n.includes('sponsoring')) return Headphones;
  if (n.includes('netzwerk') || n.includes('networking')) return Building2;
  if (n.includes('bindung') || n.includes('treue') || n.includes('loyalty')) return Heart;
  if (n.includes('angebot') || n.includes('probe') || n.includes('demo') || n.includes('sonder')) return Tag;
  if (n.includes('beratung') || n.includes('gespräch') || n.includes('konsult')) return MessageCircle;
  if (n.includes('umfrage') || n.includes('feedback') || n.includes('zufriedenheit')) return BarChart3;
  return Lightbulb;
}

// ── Helpers ──────────────────────────────────────────────────────────

function getCategorizedTable(
  table: Record<FunnelCategory, MassnahmeTableEntry[]> | MassnahmeTableEntry[] | undefined,
): Record<FunnelCategory, MassnahmeTableEntry[]> {
  if (!table) return { Awareness: [], Consideration: [], Conversion: [], Loyalty: [] };
  if (Array.isArray(table)) return { Awareness: table, Consideration: [], Conversion: [], Loyalty: [] };
  return table;
}

// ── (PhaseOverviewDesktop & PhaseOverviewMobile removed — replaced by inline accordion) ──

// ── Touchpoint Card ─────────────────────────────────────────────────

function TouchpointCard({
  measure,
  isSelected,
  onClick,
  stageColor,
  hinting,
}: {
  measure: MassnahmeTableEntry;
  isSelected: boolean;
  onClick: () => void;
  stageColor: string;
  hinting?: boolean;
}) {
  const Icon = getMeasureIcon(measure.Titel);

  return (
    <button
      onClick={onClick}
      className={cn(
        'group relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200 w-full',
        isSelected
          ? 'shadow-md bg-white'
          : 'border-gray-200 bg-white hover:shadow-md hover:-translate-y-0.5',
        hinting && !isSelected && 'animate-[hintPulse_2s_ease-in-out_infinite] z-10',
      )}
      style={isSelected ? { borderColor: stageColor } : undefined}
    >
      {measure.Rang_in_Phase != null && (
        <div
          className="absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
          style={{ backgroundColor: stageColor }}
        >
          {measure.Rang_in_Phase}
        </div>
      )}
      <div
        className={cn(
          'w-14 h-14 rounded-xl flex items-center justify-center mb-2.5 border transition-all duration-200',
          isSelected
            ? ''
            : 'border-gray-200 group-hover:border-gray-300',
        )}
        style={isSelected ? { borderColor: stageColor, backgroundColor: `${stageColor}10` } : undefined}
      >
        <Icon
          className="h-6 w-6 transition-colors duration-200"
          style={{
            color: isSelected ? stageColor : 'var(--color-charcoal)',
            opacity: isSelected ? 1 : 0.4,
          }}
        />
      </div>
      {isSelected ? (
        <div
          className="w-full py-1.5 rounded-md text-center"
          style={{ backgroundColor: stageColor }}
        >
          <span className="text-[11px] font-semibold text-white leading-none">{measure.Titel}</span>
        </div>
      ) : (
        <span className="text-[11px] text-center leading-tight font-medium transition-colors duration-200 text-[var(--color-charcoal)]/55 group-hover:text-[var(--color-charcoal)]/80">
          {measure.Titel}
        </span>
      )}
    </button>
  );
}

// ── Collapsible Step ─────────────────────────────────────────────────

function CollapsibleStep({
  step,
  index,
  compact,
  stageColor,
}: {
  step: SchrittLeitfaden;
  index: number;
  compact?: boolean;
  stageColor: string;
}) {
  const [open, setOpen] = useState(false);
  const hasDescription = !!step.Kurzbeschreibung_Leitfaden;

  return (
    <li className={cn('list-none', compact ? 'text-[13px]' : 'text-sm')}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (hasDescription) setOpen(prev => !prev); }}
        className={cn(
          'flex items-center gap-2 w-full text-left',
          hasDescription && 'cursor-pointer',
        )}
      >
        <span
          className={cn(
            'flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white',
            compact && 'w-4.5 h-4.5 text-[9px]',
          )}
          style={{ backgroundColor: stageColor }}
        >
          {index + 1}
        </span>
        <span className={cn(
          'flex-1 font-semibold leading-snug text-[var(--color-charcoal)]/75',
        )}>
          {step.Titel_Leitfaden}
        </span>
        {hasDescription && (
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 flex-shrink-0 text-[var(--color-charcoal)]/30 transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        )}
      </button>
      {hasDescription && open && (
        <p className={cn(
          'mt-1 ml-7 leading-relaxed text-[var(--color-charcoal)]/60 animate-[fadeSlideIn_0.15s_ease-out]',
          compact ? 'text-[12px]' : 'text-[13px]',
        )}>
          {step.Kurzbeschreibung_Leitfaden}
        </p>
      )}
    </li>
  );
}

// ── Measure Detail Panel ────────────────────────────────────────────

function MeasureDetailPanel({
  measure,
  stageColor,
  t,
  onPremiumClick,
}: {
  measure: MassnahmeTableEntry;
  stageColor: string;
  t: (key: string) => string;
  onPremiumClick?: () => void;
}) {
  const { analysisId } = useParams<{ analysisId: string }>();
  const description = measure.Verstaendliche_Beschreibung || '';
  const examples = measure.Konkrete_Beispiele || [];

  const openProjectPlanDialog = useProjectPlanDialogStore((s) => s.open);

  return (
    <div className="bg-white rounded-2xl border border-gray-100/80 shadow-xl overflow-hidden animate-[fadeSlideIn_0.25s_ease-out]">
      {/* Header */}
      <div className="p-4 pb-3 flex items-center gap-2.5">
        {measure.Rang_in_Phase != null && (
          <span
            className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
            style={{ backgroundColor: stageColor }}
          >
            {measure.Rang_in_Phase}
          </span>
        )}
        <h4 className="text-base font-bold text-[var(--color-charcoal)] leading-snug">
          {measure.Titel}
        </h4>
      </div>

      {/* Metadata tags */}
      {(measure.Interaktionstyp || measure.Konkretes_Format) && (
        <div className="px-4 pb-2 flex flex-wrap gap-1.5">
          {measure.Interaktionstyp && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-gray-100 text-xs font-medium text-[var(--color-charcoal)]/60">
              {t('results.cards.massnahmen.overlay.interactionType')}: {measure.Interaktionstyp}
            </span>
          )}
          {measure.Konkretes_Format && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-gray-100 text-xs font-medium text-[var(--color-charcoal)]/60">
              {t('results.cards.massnahmen.overlay.format')}: {measure.Konkretes_Format}
            </span>
          )}
        </div>
      )}

      {/* Description */}
      <div className="px-4 pb-3">
        <p className="text-sm text-[var(--color-charcoal)]/75 leading-relaxed">
          {description}
        </p>
      </div>

      {/* Praxisbeispiele list */}
      {examples.length > 0 && (
        <div className="px-4 pb-4">
          <h5 className="text-sm font-bold text-[var(--color-charcoal)] mb-2">
            {t('results.cards.massnahmen.overlay.measures')}
          </h5>
          <div className="space-y-0.5">
            {examples.map((example, i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-2">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5 text-green-500" />
                <span className="flex-1 text-sm text-[var(--color-charcoal)]/75 leading-snug">
                  {example}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Implementation Goal */}
      {measure.Umsetzungsziel && measure.Umsetzungsziel.trim() !== '' && (
        <div className="px-4 pb-3">
          <h5 className="text-xs font-bold text-[var(--color-charcoal)]/90 uppercase tracking-wide mb-1">
            {t('results.cards.massnahmen.overlay.implementationGoal')}
          </h5>
          <p className="text-sm text-[var(--color-charcoal)]/75 leading-relaxed">
            {measure.Umsetzungsziel}
          </p>
        </div>
      )}

      {/* Preparation */}
      {measure.Vorbereitung && measure.Vorbereitung.trim() !== '' && (
        <div className="px-4 pb-3">
          <h5 className="text-xs font-bold text-[var(--color-charcoal)]/90 uppercase tracking-wide mb-1">
            {t('results.cards.massnahmen.overlay.preparation')}
          </h5>
          <p className="text-sm text-[var(--color-charcoal)]/75 leading-relaxed">
            {measure.Vorbereitung}
          </p>
        </div>
      )}

      {/* Step by Step */}
      {normalizeSchritte(measure.Schritt_fuer_Schritt).length > 0 && (
        <div className="px-4 pb-3">
          <h5 className="text-xs font-bold text-[var(--color-charcoal)]/90 uppercase tracking-wide mb-1.5">
            {t('results.cards.massnahmen.overlay.stepByStep')}
          </h5>
          <ol className="space-y-2">
            {normalizeSchritte(measure.Schritt_fuer_Schritt).map((step, i) => (
              <CollapsibleStep key={i} step={step} index={i} stageColor={stageColor} />
            ))}
          </ol>
        </div>
      )}

      {/* Involved Roles */}
      {measure.Beteiligte_Rollen && measure.Beteiligte_Rollen.filter(r => r.trim()).length > 0 && (
        <div className="px-4 pb-3">
          <h5 className="text-xs font-bold text-[var(--color-charcoal)]/90 uppercase tracking-wide mb-1.5">
            {t('results.cards.massnahmen.overlay.involvedRoles')}
          </h5>
          <div className="flex flex-wrap gap-1.5">
            {measure.Beteiligte_Rollen.filter(r => r.trim()).map((role, i) => (
              <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-gray-100 text-xs font-medium text-[var(--color-charcoal)]/75">
                {role}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Third-party incentive */}
      {measure.Anreiz_fuer_Dritte && measure.Anreiz_fuer_Dritte.trim() !== '' && (
        <div className="mx-4 mb-4 p-3 rounded-xl bg-emerald-50/60 border border-emerald-200/40">
          <h5 className="text-[11px] font-bold text-emerald-800/70 uppercase tracking-wide mb-1 flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5" />
            {t('results.cards.massnahmen.overlay.thirdPartyIncentive')}
          </h5>
          <p className="text-[12px] text-emerald-700/70 leading-relaxed">
            {measure.Anreiz_fuer_Dritte}
          </p>
        </div>
      )}

      {/* CTA Buttons */}
      <div className="px-4 pb-4 flex flex-col sm:flex-row gap-2">
        <button
          className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-md"
          style={{ background: `linear-gradient(135deg, ${stageColor}, ${stageColor}cc)` }}
          onClick={onPremiumClick}
        >
          {t('results.cards.massnahmen.overlay.premiumUnlock')}
        </button>
        <button
          className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all hover:shadow-md"
          style={{ borderColor: stageColor, color: stageColor }}
          onClick={() => openProjectPlanDialog(analysisId)}
        >
          {t('results.cards.massnahmen.overlay.requestProjectPlan')}
        </button>
      </div>
    </div>
  );
}

// ── Main Overlay ────────────────────────────────────────────────────

export function MassnahmenOverlayContent({ data, getDescription, featureName, onPremiumClick }: MassnahmenOverlayContentProps) {
  const { t } = useTranslation();
  const { analysisId } = useParams<{ analysisId: string }>();
  const [activeStage, setActiveStage] = useState<FunnelCategory | null>(
    () => window.matchMedia('(min-width: 768px)').matches ? FUNNEL_CATEGORIES[0] : null,
  );
  const [selectedMeasure, setSelectedMeasure] = useState<MassnahmeTableEntry | null>(null);
  const openProjectPlanDialog = useProjectPlanDialogStore((s) => s.open);

  // Handle legacy string data
  if (typeof data === 'string') {
    return (
      <div className="space-y-6">
        <div className="hidden md:flex items-center gap-2 md:gap-3 pb-1 md:pb-2">
          <div className="p-2 md:p-2.5 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
            <Target className="h-5 w-5 md:h-6 md:w-6 text-[var(--color-primary-start)]" />
          </div>
          <h3 className="text-base md:text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">
            {t('results.cards.massnahmen.title')}
          </h3>
        </div>
        <div className="p-4 bg-[var(--glass-bg)] rounded-[var(--radius-md)] border border-[var(--glass-border)]">
          <p className="text-sm text-[var(--color-charcoal)]/80 leading-relaxed whitespace-pre-wrap">
            {data}
          </p>
        </div>
      </div>
    );
  }

  const { main } = data;
  const categorizedTable = getCategorizedTable(main.Massnahmen_Tabelle);
  const stageItems = useMemo(() => activeStage ? (categorizedTable[activeStage] || []).slice(0, 10) : [], [categorizedTable, activeStage]);
  const stageColor = activeStage ? JOURNEY_COLORS[activeStage] : '#d91680';

  // ── Inactivity hint ───────────────────────────────────────────────
  const [showHint, setShowHint] = useState(false);
  const hintTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const hasInteractedRef = useRef(false);

  const dismissHint = useCallback(() => {
    hasInteractedRef.current = true;
    setShowHint(false);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
  }, []);

  useEffect(() => {
    hintTimerRef.current = setTimeout(() => {
      if (!hasInteractedRef.current) setShowHint(true);
    }, 10_000);
    return () => clearTimeout(hintTimerRef.current);
  }, []);

  // Auto-select first item when stage changes or on initial render
  const effectiveSelectedMeasure = selectedMeasure
    ?? (stageItems.length > 0 ? stageItems[0] : null);

  const handleSelectMeasure = (measure: MassnahmeTableEntry, _index: number) => {
    dismissHint();
    setSelectedMeasure(prev => prev?.Titel === measure.Titel ? null : measure);
  };

  const handleStageChange = (stage: FunnelCategory) => {
    dismissHint();
    setActiveStage(prev => prev === stage ? null : stage);
    setSelectedMeasure(null);
  };

  return (
    <div className="space-y-6">
      {/* Hint animation keyframes */}
      <style>{`
        @keyframes hintPulse {
          0%, 100% { box-shadow: 0 0 0 0 var(--color-primary-start, #d91680); }
          50% { box-shadow: 0 0 0 4px color-mix(in srgb, var(--color-primary-start, #d91680) 18%, transparent); }
        }
      `}</style>

      {/* Header */}
      <div className="hidden md:flex items-center gap-2 md:gap-3 pb-1 md:pb-2">
        <div className="p-2 md:p-2.5 rounded-xl bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10">
          <Target className="h-5 w-5 md:h-6 md:w-6 text-[var(--color-primary-start)]" />
        </div>
        <div>
          <h3 className="text-base md:text-lg font-semibold leading-none tracking-tight text-[var(--color-charcoal)]">
            {t('results.cards.massnahmen.overlay.journeyTitle')}
          </h3>
          <p className="text-xs text-[var(--color-charcoal)]/45 mt-1.5">
            {t('results.cards.massnahmen.overlay.journeySubtitle')}
          </p>
        </div>
      </div>

      {/* Tab navigation — desktop: horizontal pills */}
      <div className="hidden md:block space-y-2 pt-2">
        <p className="text-sm font-medium text-[var(--color-charcoal)]/75">
          {t('results.cards.massnahmen.overlay.selectPhaseHint')}
        </p>
        <div className="flex gap-3 overflow-x-auto pt-1 pb-1">
          {FUNNEL_CATEGORIES.map(cat => {
            const isActive = cat === activeStage;
            const color = JOURNEY_COLORS[cat];
            return (
              <button
                key={cat}
                onClick={() => handleStageChange(cat)}
                className={cn(
                  'px-6 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 flex-shrink-0 border',
                  isActive
                    ? 'text-white shadow-md border-transparent'
                    : 'bg-white text-[var(--color-charcoal)]/70 border-gray-200 hover:border-gray-300 hover:shadow-sm hover:-translate-y-0.5',
                  showHint && !isActive && 'animate-[hintPulse_2s_ease-in-out_infinite] z-10',
                )}
                style={isActive ? { backgroundColor: color } : undefined}
              >
                {t(`results.cards.massnahmen.overlay.tabLabels.${cat.toLowerCase()}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile: phase accordion with inline measures */}
      <div className="md:hidden space-y-2.5">
        <p className="text-sm font-medium text-[var(--color-charcoal)]/55">
          {t('results.cards.massnahmen.overlay.selectPhaseHint')}
        </p>
        <div className="flex flex-col gap-2">
          {FUNNEL_CATEGORIES.map((cat, catIndex) => {
            const isActive = cat === activeStage;
            const color = JOURNEY_COLORS[cat];
            const items = (categorizedTable[cat] || []).slice(0, 10);
            const activeMeasure = isActive ? selectedMeasure : null;
            return (
              <div key={cat}>
                <button
                  onClick={() => handleStageChange(cat)}
                  className={cn(
                    'w-full flex items-center justify-between text-left px-4 py-3.5 rounded-xl transition-all duration-200',
                    isActive
                      ? 'shadow-lg scale-[1.02]'
                      : 'shadow-sm hover:shadow-md hover:scale-[1.01]',
                    showHint && !isActive && 'animate-[hintPulse_2s_ease-in-out_infinite] z-10',
                  )}
                  style={{ backgroundColor: isActive ? color : `${color}30` }}
                >
                  <div className="flex flex-col">
                    <span
                      className="text-sm font-bold leading-tight"
                      style={{ color: isActive ? '#fff' : color }}
                    >
                      {catIndex + 1}. {t(`results.cards.massnahmen.overlay.tabLabels.${cat.toLowerCase()}`)}
                    </span>
                    <span
                      className="text-[11px] leading-snug mt-0.5 font-medium"
                      style={{ color: isActive ? 'rgba(255,255,255,0.85)' : `${color}cc` }}
                    >
                      {t(`results.cards.massnahmen.card.stageHints.${cat.toLowerCase()}`)}
                    </span>
                  </div>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 flex-shrink-0 transition-transform duration-200',
                      isActive && 'rotate-180',
                    )}
                    style={{ color: isActive ? 'rgba(255,255,255,0.8)' : color }}
                  />
                </button>
                {isActive && (
                  <div className="mt-2 space-y-2 animate-[fadeSlideIn_0.2s_ease-out]">
                    {items.length === 0 ? (
                      <div className="text-center py-6 text-sm text-[var(--color-charcoal)]/35">
                        {t('results.cards.massnahmen.overlay.noMeasuresForStage')}
                      </div>
                    ) : items.map((measure, i) => {
                      const isOpen = activeMeasure?.Titel === measure.Titel;
                      const description = measure.Verstaendliche_Beschreibung || '';
                      const examples = measure.Konkrete_Beispiele || [];
                      return (
                        <div
                          key={i}
                          className={cn(
                            'rounded-xl border overflow-hidden transition-all duration-200',
                            isOpen
                              ? 'border-transparent shadow-md'
                              : 'border-gray-200 hover:border-gray-300',
                          )}
                          style={isOpen ? { borderColor: `${color}40`, boxShadow: `0 4px 20px ${color}15` } : undefined}
                        >
                          <button
                            onClick={() => handleSelectMeasure(measure, i)}
                            className="w-full flex items-center gap-3 p-3.5 text-left"
                          >
                            {measure.Rang_in_Phase != null && (
                              <span
                                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                                style={{ backgroundColor: color }}
                              >
                                {measure.Rang_in_Phase}
                              </span>
                            )}
                            <span className={cn(
                              'flex-1 text-sm font-semibold leading-snug',
                              isOpen ? 'text-[var(--color-charcoal)]' : 'text-[var(--color-charcoal)]/75',
                            )}>
                              {measure.Titel}
                            </span>
                            <ChevronDown
                              className={cn(
                                'h-4 w-4 flex-shrink-0 transition-transform duration-200',
                                isOpen ? 'rotate-180' : '',
                              )}
                              style={{ color: isOpen ? color : 'var(--color-charcoal)', opacity: isOpen ? 1 : 0.3 }}
                            />
                          </button>
                          {isOpen && (
                            <div className="animate-[fadeSlideIn_0.2s_ease-out]">
                              <div className="h-px mx-3.5" style={{ backgroundColor: `${color}20` }} />
                              {(measure.Interaktionstyp || measure.Konkretes_Format) && (
                                <div className="px-3.5 pt-2.5 flex flex-wrap gap-1.5">
                                  {measure.Interaktionstyp && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-[11px] font-medium text-[var(--color-charcoal)]/60">
                                      {t('results.cards.massnahmen.overlay.interactionType')}: {measure.Interaktionstyp}
                                    </span>
                                  )}
                                  {measure.Konkretes_Format && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-[11px] font-medium text-[var(--color-charcoal)]/60">
                                      {t('results.cards.massnahmen.overlay.format')}: {measure.Konkretes_Format}
                                    </span>
                                  )}
                                </div>
                              )}
                              {description && (
                                <div className="px-3.5 pt-2.5 pb-2">
                                  <p className="text-[13px] text-[var(--color-charcoal)]/75 leading-relaxed">
                                    {description}
                                  </p>
                                </div>
                              )}
                              {examples.length > 0 && (
                                <div className="px-3.5 pb-3">
                                  <h5 className="text-xs font-bold text-[var(--color-charcoal)]/90 uppercase tracking-wide mb-1.5">
                                    {t('results.cards.massnahmen.overlay.measures')}
                                  </h5>
                                  <ul className="space-y-1">
                                    {examples.map((example, j) => (
                                        <li key={j} className="flex items-start gap-2 text-[13px] leading-snug">
                                          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-green-500" />
                                          <span className="text-[var(--color-charcoal)]/75">{example}</span>
                                        </li>
                                      ))}
                                  </ul>
                                </div>
                              )}
                              {measure.Umsetzungsziel && measure.Umsetzungsziel.trim() !== '' && (
                                <div className="px-3.5 pt-1 pb-2">
                                  <h5 className="text-[11px] font-bold text-[var(--color-charcoal)]/90 uppercase tracking-wide mb-0.5">
                                    {t('results.cards.massnahmen.overlay.implementationGoal')}
                                  </h5>
                                  <p className="text-[13px] text-[var(--color-charcoal)]/75 leading-relaxed">
                                    {measure.Umsetzungsziel}
                                  </p>
                                </div>
                              )}
                              {measure.Vorbereitung && measure.Vorbereitung.trim() !== '' && (
                                <div className="px-3.5 pt-1 pb-2">
                                  <h5 className="text-[11px] font-bold text-[var(--color-charcoal)]/90 uppercase tracking-wide mb-0.5">
                                    {t('results.cards.massnahmen.overlay.preparation')}
                                  </h5>
                                  <p className="text-[13px] text-[var(--color-charcoal)]/75 leading-relaxed">
                                    {measure.Vorbereitung}
                                  </p>
                                </div>
                              )}
                              {normalizeSchritte(measure.Schritt_fuer_Schritt).length > 0 && (
                                <div className="px-3.5 pt-1 pb-2">
                                  <h5 className="text-[11px] font-bold text-[var(--color-charcoal)]/90 uppercase tracking-wide mb-1">
                                    {t('results.cards.massnahmen.overlay.stepByStep')}
                                  </h5>
                                  <ol className="space-y-1.5">
                                    {normalizeSchritte(measure.Schritt_fuer_Schritt).map((step, j) => (
                                      <CollapsibleStep key={j} step={step} index={j} compact stageColor={color} />
                                    ))}
                                  </ol>
                                </div>
                              )}
                              {measure.Beteiligte_Rollen && measure.Beteiligte_Rollen.filter(r => r.trim()).length > 0 && (
                                <div className="px-3.5 pt-1 pb-2">
                                  <h5 className="text-[11px] font-bold text-[var(--color-charcoal)]/90 uppercase tracking-wide mb-1">
                                    {t('results.cards.massnahmen.overlay.involvedRoles')}
                                  </h5>
                                  <div className="flex flex-wrap gap-1">
                                    {measure.Beteiligte_Rollen.filter(r => r.trim()).map((role, j) => (
                                      <span key={j} className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-[11px] font-medium text-[var(--color-charcoal)]/75">
                                        {role}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {measure.Anreiz_fuer_Dritte && measure.Anreiz_fuer_Dritte.trim() !== '' && (
                                <div className="mx-3.5 mb-3 p-2.5 rounded-lg bg-emerald-50/60 border border-emerald-200/30">
                                  <h5 className="text-[10px] font-bold text-emerald-800/60 uppercase tracking-wide mb-0.5 flex items-center gap-1">
                                    <Info className="h-3 w-3" />
                                    {t('results.cards.massnahmen.overlay.thirdPartyIncentive')}
                                  </h5>
                                  <p className="text-[12px] text-emerald-700/60 leading-relaxed">
                                    {measure.Anreiz_fuer_Dritte}
                                  </p>
                                </div>
                              )}
                              <div className="px-3.5 pb-3.5 flex gap-2">
                                <button
                                  className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold text-white transition-all hover:shadow-md"
                                  style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
                                  onClick={(e) => { e.stopPropagation(); onPremiumClick?.(); }}
                                >
                                  {t('results.cards.massnahmen.overlay.premiumUnlock')}
                                </button>
                                <button
                                  className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all hover:shadow-md"
                                  style={{ borderColor: color, color }}
                                  onClick={(e) => { e.stopPropagation(); openProjectPlanDialog(analysisId); }}
                                >
                                  {t('results.cards.massnahmen.overlay.requestProjectPlan')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop: content area with touchpoint cards + detail panel */}
      <div className="hidden md:block">
        {stageItems.length === 0 ? (
          <div className="text-center py-12 text-sm text-[var(--color-charcoal)]/35">
            {t('results.cards.massnahmen.overlay.noMeasuresForStage')}
          </div>
        ) : (
          <div className={cn(
            'grid gap-6 transition-all duration-300',
            effectiveSelectedMeasure ? 'grid-cols-[220px_1fr]' : 'grid-cols-1',
          )}>
            <div className={cn(
              'grid gap-3 content-start',
              effectiveSelectedMeasure ? 'grid-cols-1' : 'grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
            )}>
              {stageItems.map((measure, i) => (
                  <TouchpointCard
                    key={i}
                    measure={measure}
                    isSelected={effectiveSelectedMeasure?.Titel === measure.Titel}
                    onClick={() => handleSelectMeasure(measure, i)}
                    stageColor={stageColor}
                    hinting={showHint}
                  />
              ))}
            </div>
            {effectiveSelectedMeasure && (
              <div className="min-w-0">
                <div className="sticky top-4">
                  <MeasureDetailPanel
                    key={effectiveSelectedMeasure.Titel}
                    measure={effectiveSelectedMeasure}
                    stageColor={stageColor}
                    t={t}
                    onPremiumClick={onPremiumClick}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary */}
      {main.Massnahmen_Zusammenfassung && main.Massnahmen_Zusammenfassung !== '[placeholder]' && (
        <div className="pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5 text-[var(--color-primary-start)]" />
            <h4 className="text-lg font-semibold text-[var(--color-charcoal)]">
              {t('results.cards.massnahmen.overlay.summary')}
            </h4>
            <SectionDescriptionText getDescription={getDescription} featureName={featureName} sectionKey="summary" />
          </div>
          <div className="p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-gray-100/80">
            <p className="text-sm text-[var(--color-charcoal)]/70 leading-relaxed">
              {main.Massnahmen_Zusammenfassung}
            </p>
          </div>
        </div>
      )}

    </div>
  );
}

export default MassnahmenOverlayContent;
